/**
 * State store for the notifications feature.
 * Manages notification list, unread count, popup visibility, and FCM initialization.
 */

import type { NotificationsContainer } from '@elara/client-core/containers/NotificationsContainer';
import type { AppFlowsContainer } from '@elara/client-core/flows';
import type { Notification } from '@elara/client-core/models/NotificationModel';
import type { SessionState } from '@elara/client-core/states/SessionState';

import { effect } from '@preact/signals-react';

import { createState } from '@elara/client-core/lib/signals-utils';

import { showWebNotification } from '#lib/web-notifications.js';

/** Message the service worker posts to route a notification click inside an open tab. */
const SW_NAVIGATE_MESSAGE = 'elara:navigate';

export function createNotificationsState(
  sessionState: SessionState,
  notifications: NotificationsContainer,
  flows: AppFlowsContainer,
  /** In-app navigation, for notification clicks the service worker hands to an open tab. */
  navigate: (path: string) => void,
) {
  /** Track known notification IDs to detect newly arrived ones */
  const knownIds = new Set<string>();
  let initialized = false;
  let fcmCleanup: (() => void) | undefined;
  let fcmInitialized = false;

  const state = createState({
    /** Whether the notifications popup is open */
    isOpen: false,

    /** Reactive Firestore query for the current user's notifications */
    get notifications() {
      const userId = sessionState.userId;
      if (!userId) return undefined;
      return notifications.ClientNotification.list({ userId });
    },

    /** List of notification items (empty array if not ready) */
    get items(): Notification[] {
      const query = this.notifications;
      if (!query || !query.isReady) return [];
      return query.items as Notification[];
    },

    /** Count of unread notifications */
    get unreadCount(): number {
      return this.items.filter((n) => !n.read).length;
    },

    /** Whether there are unread notifications */
    get hasUnread(): boolean {
      return this.unreadCount > 0;
    },

    /** Toggle the notifications popup */
    toggleOpen() {
      this.isOpen = !this.isOpen;
    },

    /** Close the notifications popup */
    close() {
      this.isOpen = false;
    },

    /** Mark a single notification as read */
    async markRead(notificationId: string) {
      try {
        await notifications.ClientNotification.markRead({ notificationId });
      } catch (error) {
        console.warn('[NotificationsState] Failed to mark notification as read:', error);
      }
    },

    /** Mark all notifications as read */
    async markAllRead() {
      try {
        await notifications.ClientNotification.markAllRead({});
      } catch (error) {
        console.warn('[NotificationsState] Failed to mark all notifications as read:', error);
      }
    },

    /**
     * Initialize FCM — call after permission is granted from a user gesture. Runs even when the
     * sign-in pass already ran: that pass skips token registration while permission isn't granted.
     */
    async initFCM() {
      fcmInitialized = true;
      fcmCleanup?.();
      fcmCleanup = undefined;
      await initFCM();
    },

    /** Start monitoring — watches for new notifications and initializes FCM */
    startMonitors() {
      const disposeFCM = effect(() => {
        const userId = sessionState.userId;
        if (userId && !fcmInitialized) {
          fcmInitialized = true;
          void initFCM();
        }
      });

      const disposeWebNotifications = effect(() => {
        const items = state.items;
        if (!initialized) {
          for (const item of items) knownIds.add(item.id);
          initialized = true;
        } else {
          for (const item of items) {
            if (!knownIds.has(item.id) && !item.read) {
              knownIds.add(item.id);
              showWebNotification(item.title, item.body ?? undefined, item.url);
            }
          }
        }
      });

      // A notification click routed to this tab by the service worker: navigate in place, no reload.
      const handleWorkerMessage = (event: MessageEvent<unknown>) => {
        const data = event.data as { type?: unknown; url?: unknown } | undefined;
        if (data?.type !== SW_NAVIGATE_MESSAGE || typeof data.url !== 'string') return;
        const url = new URL(data.url, window.location.origin);
        if (url.origin !== window.location.origin) return;
        navigate(`${url.pathname}${url.search}${url.hash}`);
      };
      const workers = typeof navigator === 'undefined' ? undefined : navigator.serviceWorker;
      workers?.addEventListener('message', handleWorkerMessage);

      return () => {
        workers?.removeEventListener('message', handleWorkerMessage);
        disposeFCM();
        disposeWebNotifications();
        fcmCleanup?.();
        fcmCleanup = undefined;
        fcmInitialized = false;
      };
    },
  });

  async function initFCM() {
    try {
      const { initializeFCM } = await import('#firebase/messaging.js');
      fcmCleanup = await initializeFCM(flows);
    } catch (error) {
      console.warn('[NotificationsState] FCM initialization failed:', error);
    }
  }

  return state;
}

export type NotificationsState = ReturnType<typeof createNotificationsState>;
